import { ChannelType, ChatType } from '@/proto/agentplatform/v1/channel_pb';

/** 各环境的 Bridge (WebSocket) 连接地址。 */
const BRIDGE_CONNECTION_URLS = {
  dev: 'wss://infini-agent.cloud-dev.infini-ai.com:9443/ws/v1',
  qa: 'wss://infini-agent-qa.cloud-dev.infini-ai.com:9443/ws/v1',
  prod: 'wss://infini-agent.cloud.infini-ai.com/ws/v1',
} as const;

type AppEnv = keyof typeof BRIDGE_CONNECTION_URLS;

/** 环境判定：优先取构建期注入的 VITE_APP_ENV，兜底使用运行时 hostname。 */
function resolveAppEnv(): AppEnv {
  const explicit = (import.meta.env.VITE_APP_ENV as string | undefined)?.toLowerCase();
  if (explicit === 'dev' || explicit === 'qa' || explicit === 'prod') {
    return explicit;
  }
  if (import.meta.env.DEV) {
    return 'dev';
  }
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (/(^|[.-])qa([.-]|$)/.test(host)) {
    return 'qa';
  }
  if (host.includes('cloud-dev.infini-ai.com')) {
    return 'dev';
  }
  return 'prod';
}

/** Bridge (WebSocket) 连接地址：详情页与列表页共用。 */
export const BRIDGE_CONNECTION_URL = BRIDGE_CONNECTION_URLS[resolveAppEnv()];

export interface ChannelTypeMeta {
  type: ChannelType;
  label: string;
  desc: string;
  /** DsIcon 名称（lucide） */
  icon: string;
}

/** 左侧类型导航项（对齐参考页顺序：WebSocket / 飞书 / QQ）。 */
export const CHANNEL_TYPES: ChannelTypeMeta[] = [
  { type: ChannelType.BRIDGE, label: 'WebSocket', desc: 'H5 / 前端长连接', icon: 'cable' },
  { type: ChannelType.FEISHU, label: '飞书', desc: '企业内部 IM', icon: 'message-circle' },
  { type: ChannelType.QQ, label: 'QQ', desc: '社交渠道', icon: 'message-square' },
];

export function channelTypeLabel(t: ChannelType) {
  switch (t) {
    case ChannelType.FEISHU:
      return '飞书';
    case ChannelType.QQ:
      return 'QQ';
    case ChannelType.BRIDGE:
      return 'WebSocket';
    default:
      return ChannelType[t] ?? '—';
  }
}

/** 每行或逗号分隔的用户/群组 ID，空表示不限制来源（与后端 p2p/group allow 逻辑一致） */
export function parseIdList(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formatIdList(ids: readonly string[]): string {
  return [...ids].filter(Boolean).join('\n');
}

/** 与 Get 接口掩码一致，比较是否修改密钥时视为「未填写」 */
export function credentialSecretComparable(secret: string): string {
  const t = secret.trim();
  return t === 'xxxxxx' ? '' : t;
}

/** 与 worker 注册能力一致时取自渠道列表；若无 capabilities 数据则返回 null */
export function channelCapabilitiesImplyGroupChat(
  capabilities: { chatTypes: ChatType[] } | undefined
): boolean | null {
  const types = capabilities?.chatTypes;
  if (types == null || types.length === 0) {
    return null;
  }
  return types.includes(ChatType.GROUP);
}

/** 列表尚未同步或无能力字段时的兜底（与 extension 驱动注册保持一致） */
export function channelTypeConventionallySupportsGroup(t: ChannelType): boolean {
  return t === ChannelType.FEISHU;
}
